import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

import type { CampaignsResponse, CampaignTypes, CampaignSource, CampaignPatch } from '../types/campaign.types';
import { PRIZE_POP_CAMPAIGN_TYPE, TOURNAMENTS_CAMPAIGN_TYPE, REBATE_CAMPAIGN_TYPE } from '../types/campaign.types';
import type { CampaignPanelInfo } from '../components/MultiViewCampaignSection/types';
import { useAppStore, useViewMode } from 'store/app/appStore';
import { SINGLE_VIEW_MODE } from '../../../constants';
import { isCampaign, isOfType } from '../utils/campaignHelpers';
import { isTournamentSource, parseTournamentsData, updateTournamentGameFlags } from '../utils/variants/tournamentHelpers';
import { isPrizePopSource } from '../utils/variants/prizePopHelpers';
import type { ITournament, IParsedTournament } from '../types/variants/tournament.types';
import { processTournamentCampaigns, processCampaigns, computePanelInfoFields } from './helpers/campaignsProcessing';
import { useCampaignTypeContext } from './context/CampaignTypeContext';
import { isRebateSource } from '../utils/variants/rebateHelpers';

export { isCampaign, isTournamentSource, isPrizePopSource, isRebateSource };

// Recompute the per-type `hasMany` record from a campaigns array, using the
// same count-per-type logic as `setCampaignsFromResponse`. Kept exhaustive over
// the `CampaignTypes` union so single-vs-multi view stays correct for QA mocks.
const computeHasMany = (campaigns: CampaignSource[]): Record<CampaignTypes, boolean> =>
  ({
    [PRIZE_POP_CAMPAIGN_TYPE]: campaigns.filter(c => isOfType(c, PRIZE_POP_CAMPAIGN_TYPE)).length > 1,
    [TOURNAMENTS_CAMPAIGN_TYPE]: campaigns.filter(c => isOfType(c, TOURNAMENTS_CAMPAIGN_TYPE)).length > 1,
    [REBATE_CAMPAIGN_TYPE]: campaigns.filter(c => isOfType(c, REBATE_CAMPAIGN_TYPE)).length > 1
  }) as Record<CampaignTypes, boolean>;

interface CampaignsState {
  // Core state
  campaigns: CampaignSource[];
  selectedCampaignIds: Record<string, string | null>;
  campaignType: CampaignTypes;

  // UI state
  isHeaderCollapsed: Record<CampaignTypes, boolean>;
  isActiveSlot: boolean;
  panelInfos: Record<CampaignTypes, CampaignPanelInfo>;
  campaignInfoVisible: Record<string, boolean>;

  // Meta info from API
  autoJoin: Record<CampaignTypes, boolean>;
  playerName: string;

  // Computed state - hasMany tracks if campaigns by type are more than 1
  hasMany: Record<CampaignTypes, boolean>;

  /**
   * QA-only flag mirroring `tournamentsStore.mockMode`. When `true`:
   *   - `setCampaignsFromResponse` early-returns (defense-in-depth twin to
   *     the `!mockMode` gate inside `useCampaignsQuery`'s `enabled` flag).
   *   - QA-driven seeds go through `seedExternalCampaigns`, which is NOT
   *     gated and replaces the prize_pop slice of the list.
   *   - Synthetic entries are injected via `addMockCampaign` and pinned to
   *     the end of the existing list of the same campaignType (no sort).
   * Flip via `setMockMode(true)` / `clearMockCampaigns` (which also flips
   * the flag back off).
   */
  mockMode: boolean;


  // Actions
  setCampaignsFromResponse: (response: CampaignsResponse, type?: CampaignTypes) => void;
  setSelectedCampaignId: (id: string | null, type?: CampaignTypes) => void;
  setCampaignType: (type: CampaignTypes) => void;
  toggleHeaderCollapsed: (type: CampaignTypes) => void;
  setActiveSlot: (active: boolean) => void;
  setPanelInfo: (data: CampaignPanelInfo, type?: CampaignTypes) => void;
  getPanelInfo: (type?: CampaignTypes) => CampaignPanelInfo;
  clearCampaigns: () => void;
  updateCampaign: (campaignId: string, updates: CampaignPatch, type?: CampaignTypes) => void;
  reParseTournamentsWithGames: () => void;
  setCampaignInfoVisible: (id: string, isVisible: boolean) => void;
  clearCampaignInfoVisible: (id: string) => void;

  /* QA-only — see `mockMode` above. */
  setMockMode: (enabled: boolean) => void;
  /**
   * Bypass door used by the QA panel's "fetch with pasted creds" flow.
   * Replaces every campaign of the same campaignType as the first entry
   * (preserving entries of other types), no sort. NOT gated by mockMode —
   * it's the explicit "load from QA-pasted creds" door used while
   * mockMode is already on (so `setCampaignsFromResponse` would early-return).
   */
  seedExternalCampaigns: (list: CampaignSource[]) => void;
  /**
   * Append a synthetic campaign to the end of the existing list (same
   * campaignType, no sort). Does NOT toggle mockMode — callers must
   * `setMockMode(true)` first; the QA panel UI gates this behind enableMocking.
   */
  addMockCampaign: (campaign: CampaignSource) => void;
  /** Remove a single campaign by id (real or mocked). */
  removeCampaign: (id: string) => void;
  /**
   * In-place patch by id. No re-sort, no derived-field recompute. Mirrors
   * `updateCampaign` but exposed under a distinct name so QA call sites
   * don't have to reach into the existing per-event update helper.
   */
  updateCampaignById: (id: string, patch: CampaignPatch) => void;
  /**
   * Filter out every campaign whose id starts with `qa-mock-` and flip
   * `mockMode` back to false. Companion to `setMockMode(true)`; called by
   * the QA panel's "Disable mocking" CTA.
   */
  clearMockCampaigns: () => void;

  // Computed getters
  getSelectedCampaignId: (type?: CampaignTypes) => string | null;
  getSelectedCampaign: (type?: CampaignTypes) => CampaignSource | undefined;
  getPrizesCount: () => { total: number; left: number };
}

export const useCampaignsStore = create<CampaignsState>()(
  devtools<CampaignsState>(
    (set, get) => ({
      // Core state
      campaigns: [],
      selectedCampaignIds: {},
      campaignType: PRIZE_POP_CAMPAIGN_TYPE,

      // UI state
      isHeaderCollapsed: {
        [PRIZE_POP_CAMPAIGN_TYPE]: false,
        [TOURNAMENTS_CAMPAIGN_TYPE]: false,
        [REBATE_CAMPAIGN_TYPE]: false,
      } as Record<CampaignTypes, boolean>,
      isActiveSlot: false,
      panelInfos: {
        prize_pop: {
          backgroundImage: undefined,
          backgroundColor: undefined,
          backgroundOverlayImage: undefined,
          logo: undefined,
          rangeBackground: '',
          prizeFund: '',
          prizeCurrency: '',
          headerTitle: '',
        },
        tournaments: {
          backgroundImage: undefined,
          backgroundColor: undefined,
          backgroundOverlayImage: undefined,
          logo: undefined,
          rangeBackground: '',
          prizeFund: '',
          prizeCurrency: '',
          headerTitle: '',
        },
        rebate: {
          backgroundImage: undefined,
          backgroundColor: undefined,
          backgroundOverlayImage: undefined,
          logo: undefined,
          rangeBackground: '',
          prizeFund: '',
          prizeCurrency: '',
          headerTitle: '',
        },
      } as Record<CampaignTypes, CampaignPanelInfo>,
      campaignInfoVisible: {},

      // Meta info
      autoJoin: {
        [PRIZE_POP_CAMPAIGN_TYPE]: false,
        [TOURNAMENTS_CAMPAIGN_TYPE]: false,
        [REBATE_CAMPAIGN_TYPE]: false,
      } as Record<CampaignTypes, boolean>,
      playerName: '',

      // Computed state - hasMany tracks if campaigns by type are more than 1
      hasMany: {
        [PRIZE_POP_CAMPAIGN_TYPE]: false,
        [TOURNAMENTS_CAMPAIGN_TYPE]: false,
        [REBATE_CAMPAIGN_TYPE]: false,
      } as Record<CampaignTypes, boolean>,

      // QA-only — see CampaignsState.mockMode JSDoc.
      mockMode: false,

      setCampaignsFromResponse: (response, type) => {
        // QA mocking guard — defense-in-depth twin to the `!mockMode` gate
        // inside `useCampaignsQuery`'s `enabled` flag. Even if a query
        // refetch slips through, it can't wipe synthetic mocks.
        if (get().mockMode) {
          return;
        }
        const state = get();
        const resolvedType = type || state.campaignType;
        const currentSelectedId = state.selectedCampaignIds[resolvedType];

        let mergedCampaigns: CampaignSource[];
        let newSelectedId: string | null;

        if (resolvedType === TOURNAMENTS_CAMPAIGN_TYPE) {
          const result = processTournamentCampaigns(
            response,
            state.campaigns,
            currentSelectedId
          );
          mergedCampaigns = result.mergedCampaigns;
          newSelectedId = result.newSelectedId;
        } else {
          const result = processCampaigns(
            response,
            resolvedType,
            state.campaigns,
            currentSelectedId
          );
          mergedCampaigns = result.mergedCampaigns;
          newSelectedId = result.newSelectedId;
        }

        const campaignsForType = mergedCampaigns.filter(c => isOfType(c, resolvedType));
        const { prizeFund, prizeCurrency, headerTitle } = computePanelInfoFields(campaignsForType, resolvedType);

        // Update hasMany for the resolved type
        const newHasMany = {
          ...state.hasMany,
          [resolvedType]: campaignsForType.length > 1
        };

        // autoJoin is keyed per type so a concurrent fetch of the other
        // campaign type can't clobber this type's flag (last-writer-wins race).
        const newAutoJoin = {
          ...state.autoJoin,
          [resolvedType]: response.autoJoin,
        };

        set(
          {
            campaigns: mergedCampaigns,
            autoJoin: newAutoJoin,
            playerName: response.playerName,
            selectedCampaignIds: {
              ...state.selectedCampaignIds,
              [resolvedType]: newSelectedId,
            },
            panelInfos: {
              ...state.panelInfos,
              [resolvedType]: {
                ...state.panelInfos[resolvedType],
                type: resolvedType,
                headerTitle,
                prizeFund,
                prizeCurrency,
              },
            },
            hasMany: newHasMany,
          },
          false,
          `setCampaigns/${resolvedType}`,
        );
      },

      setSelectedCampaignId: (id, type) => {
        const state = get();
        const resolvedType = type || state.campaignType;
        set(
          {
            selectedCampaignIds: {
              ...state.selectedCampaignIds,
              [resolvedType]: id,
            },
          },
          false,
          `setSelectedCampaignId/${resolvedType}`,
        );
      },

      setCampaignType: (type) => {
        set({ campaignType: type }, false, 'setCampaignType');
      },

      toggleHeaderCollapsed: (type) => {
        set((state) => ({ isHeaderCollapsed: { ...state.isHeaderCollapsed, [type]: !state.isHeaderCollapsed[type] } }), false, 'toggleHeaderCollapsed');
      },

      setActiveSlot: (active) => {
        set({ isActiveSlot: active }, false, 'setActiveSlot');
      },

      setPanelInfo: (data, type) => {
        const state = get();
        const resolvedType = type || data.type || state.campaignType;
        set(
          {
            panelInfos: {
              ...state.panelInfos,
              [resolvedType]: { ...data, isPanel: true },
            },
          },
          false,
          `setPanelInfo/${resolvedType}`,
        );
      },

      getPanelInfo: (type) => {
        const state = get();
        const resolvedType = type || state.campaignType;
        return state.panelInfos[resolvedType];
      },

      clearCampaigns: () => {
        set(
          {
            campaigns: [],
            selectedCampaignIds: {},
            isHeaderCollapsed: {
              [PRIZE_POP_CAMPAIGN_TYPE]: false,
              [TOURNAMENTS_CAMPAIGN_TYPE]: false,
              [REBATE_CAMPAIGN_TYPE]: false,
            } as Record<CampaignTypes, boolean>,
            isActiveSlot: false,
            panelInfos: {
              prize_pop: {
                backgroundImage: undefined,
                backgroundColor: undefined,
                backgroundOverlayImage: undefined,
                logo: undefined,
                rangeBackground: '',
                prizeFund: '',
                prizeCurrency: '',
                headerTitle: '',
              },
              tournaments: {
                backgroundImage: undefined,
                backgroundColor: undefined,
                backgroundOverlayImage: undefined,
                logo: undefined,
                rangeBackground: '',
                prizeFund: '',
                prizeCurrency: '',
                headerTitle: '',
              },
              rebate: {
                backgroundImage: undefined,
                backgroundColor: undefined,
                backgroundOverlayImage: undefined,
                logo: undefined,
                rangeBackground: '',
                prizeFund: '',
                prizeCurrency: '',
                headerTitle: '',
              },
            } as Record<CampaignTypes, CampaignPanelInfo>,
            autoJoin: {
              [PRIZE_POP_CAMPAIGN_TYPE]: false,
              [TOURNAMENTS_CAMPAIGN_TYPE]: false,
              [REBATE_CAMPAIGN_TYPE]: false,
            } as Record<CampaignTypes, boolean>,
            playerName: '',
            hasMany: {
              [PRIZE_POP_CAMPAIGN_TYPE]: false,
              [TOURNAMENTS_CAMPAIGN_TYPE]: false,
              [REBATE_CAMPAIGN_TYPE]: false,
            } as Record<CampaignTypes, boolean>,
          },
          false,
          'clearCampaigns',
        );
      },

      setCampaignInfoVisible: (id, isVisible) => {
        set((state) => {
          if (state.campaignInfoVisible[id] === isVisible) return state;
          return { campaignInfoVisible: { ...state.campaignInfoVisible, [id]: isVisible } };
        }, false, 'setCampaignInfoVisible');
      },

      clearCampaignInfoVisible: (id) => {
        set((state) => {
          const { [id]: _, ...rest } = state.campaignInfoVisible;
          return { campaignInfoVisible: rest };
        }, false, 'clearCampaignInfoVisible');
      },

      updateCampaign: (campaignId, updates, type) => {
        set(
          (state) => ({
            campaigns: state.campaigns.map((c) =>
              c.id === campaignId && (!type || isOfType(c, type))
                ? { ...c, ...updates } as CampaignSource
                : c
            ),
          }),
          false,
          'updateCampaign',
        );
      },

      reParseTournamentsWithGames: () => {
        const state = get();
        const tournaments = state.campaigns.filter(isTournamentSource) as (ITournament | IParsedTournament)[];

        if (tournaments.length === 0) return;

        const normalizedGames = useAppStore.getState().supportedGames;
        if (!normalizedGames || Object.keys(normalizedGames).length === 0) return;

        const parsed = parseTournamentsData(tournaments);

        updateTournamentGameFlags(parsed);

        // Preserve the existing UI order — re-parsing should only refresh game data,
        // not re-shuffle the list (which would make joined cards jump to the top).
        const kept = state.campaigns.filter(c => !isTournamentSource(c));

        const newHasMany = {
          ...state.hasMany,
          [TOURNAMENTS_CAMPAIGN_TYPE]: parsed.length > 1
        };

        set(
          {
            campaigns: [...kept, ...parsed],
            hasMany: newHasMany,
          },
          false,
          'reParseTournamentsWithGames',
        );
      },

      getSelectedCampaignId: (type) => {
        const state = get();
        const resolvedType = type || state.campaignType;
        return state.selectedCampaignIds[resolvedType] ?? null;
      },

      getSelectedCampaign: (type) => {
        const state = get();
        const resolvedType = type || state.campaignType;
        const selectedId = state.selectedCampaignIds[resolvedType];
        if (!selectedId) return undefined;
        return state.campaigns.find((c) => c.id === selectedId);
      },

      /* ── QA mocking surface ─────────────────────────────────────── */

      setMockMode: (enabled) => {
        set({ mockMode: enabled }, false, 'setMockMode');
      },

      seedExternalCampaigns: (list) => {
        // Mirrors tournamentsStore.seedExternalTournaments: NOT gated by
        // mockMode. Replaces every campaign whose type matches the first
        // entry's type (keeping campaigns of other types intact).
        const state = get();
        if (list.length === 0) {
          set({ campaigns: state.campaigns }, false, 'seedExternalCampaigns');
          return;
        }
        const firstType = isCampaign(list[0]) ? list[0].type : undefined;
        const preservedOther = state.campaigns.filter((c) => {
          if (!firstType) return true;
          if (!isCampaign(c)) return true;
          return c.type !== firstType;
        });
        const newList = [...preservedOther, ...list];
        // Preserve existing per-type selection; only set a default for the seeded
        // type if no campaign of that type is currently selected.
        let selectedCampaignIds = state.selectedCampaignIds;
        if (firstType && !selectedCampaignIds[firstType] && list.length > 0) {
          selectedCampaignIds = { ...selectedCampaignIds, [firstType]: list[0].id };
        }
        set(
          { campaigns: newList, selectedCampaignIds, hasMany: computeHasMany(newList) },
          false,
          'seedExternalCampaigns',
        );
      },

      addMockCampaign: (campaign) => {
        // Pin to the end of the same-type slice — no resort, no merge.
        // Mirrors the no-reorder-on-user-action rule for the carousel.
        const state = get();
        const before: CampaignSource[] = [];
        const sameType: CampaignSource[] = [];
        const after: CampaignSource[] = [];
        let inSameType = false;
        let seenSameType = false;
        for (const c of state.campaigns) {
          const isSame = isCampaign(c) && c.type === campaign.type;
          if (isSame) {
            sameType.push(c);
            inSameType = true;
            seenSameType = true;
          } else if (!seenSameType) {
            before.push(c);
          } else {
            after.push(c);
          }
          void inSameType;
        }
        const newCampaigns: CampaignSource[] = seenSameType
          ? [...before, ...sameType, campaign, ...after]
          : [...state.campaigns, campaign];
        // Set a default selection for this type only if none exists
        let selectedCampaignIds = state.selectedCampaignIds;
        if (campaign.type && !selectedCampaignIds[campaign.type]) {
          selectedCampaignIds = { ...selectedCampaignIds, [campaign.type]: campaign.id };
        }
        set(
          { campaigns: newCampaigns, selectedCampaignIds, hasMany: computeHasMany(newCampaigns) },
          false,
          'addMockCampaign',
        );
      },

      removeCampaign: (id) => {
        set(
          (state) => {
            const next = state.campaigns.filter((c) => c.id !== id);
            // Clear any selectedCampaignId slot pointing at the removed campaign
            const selectedCampaignIds = Object.fromEntries(
              Object.entries(state.selectedCampaignIds).map(([type, selectedId]) =>
                [type, selectedId === id ? null : selectedId],
              ),
            );
            return { campaigns: next, selectedCampaignIds, hasMany: computeHasMany(next) };
          },
          false,
          'removeCampaign',
        );
      },

      updateCampaignById: (id, patch) => {
        set(
          (state) => ({
            campaigns: state.campaigns.map((c) =>
              c.id === id ? ({ ...c, ...patch } as CampaignSource) : c
            ),
          }),
          false,
          'updateCampaignById',
        );
      },

      clearMockCampaigns: () => {
        set(
          (state) => {
            const filtered = state.campaigns.filter((c) => !c.id.startsWith('qa-mock-'));
            // Clear any selectedCampaignId slot pointing at a removed mock campaign
            const remainingIds = new Set(filtered.map((c) => c.id));
            const selectedCampaignIds = Object.fromEntries(
              Object.entries(state.selectedCampaignIds).map(([type, selectedId]) =>
                [type, selectedId && remainingIds.has(selectedId) ? selectedId : null],
              ),
            );
            return {
              campaigns: filtered,
              selectedCampaignIds,
              mockMode: false,
              hasMany: computeHasMany(filtered),
            };
          },
          false,
          'clearMockCampaigns',
        );
      },

      getPrizesCount: () => {
        const state = get();
        const selectedId = state.selectedCampaignIds[PRIZE_POP_CAMPAIGN_TYPE];
        if (!selectedId) return { total: 0, left: 0 };

        const campaign = state.campaigns.find((c) => c.id === selectedId);
        if (!campaign || !isCampaign(campaign)) return { total: 0, left: 0 };

        const total = campaign.totalPrizesCount;
        const won = campaign.prizesWonCount;

        if (total === undefined || won === undefined) return { total: 0, left: 0 };

        const left = total - won;
        return { total, left };
      },
    }),
    { name: 'CampaignsStore' },
  ),
);

// Selector hooks
export const useCampaigns = (type?: CampaignTypes) => {
  const resolvedType = type || PRIZE_POP_CAMPAIGN_TYPE;
  return useCampaignsStore((state) => {
    switch (resolvedType) {
      case TOURNAMENTS_CAMPAIGN_TYPE:
        return state.campaigns.filter(isTournamentSource);
      case REBATE_CAMPAIGN_TYPE:
        return state.campaigns.filter(isRebateSource);
      default:
        return state.campaigns.filter(isPrizePopSource);
    }
  });
};

export const useCampaign = (campaignId: string, type?: CampaignTypes) => {
  const resolvedType = type || PRIZE_POP_CAMPAIGN_TYPE;
  return useCampaignsStore((state) => 
    state.campaigns.find(c => c.id === campaignId && isOfType(c, resolvedType))
  );
};

export const useCampaignTypeById = (campaignId?: string) =>
  useCampaignsStore((state) =>
    campaignId ? state.campaigns.find(c => c.id === campaignId)?.type : undefined
  );

export const useSelectedCampaign = (type?: CampaignTypes) => {
  return useCampaignsStore((state) => state.getSelectedCampaign(type));
};

export const useCampaignType = () => useCampaignsStore((state) => state.campaignType);

const useResolvedCampaignType = (type?: CampaignTypes): CampaignTypes => {
  const contextType = useCampaignTypeContext();
  const globalType = useCampaignType();
  return type ?? contextType ?? globalType;
};

export const useAutoJoin = (type?: CampaignTypes) => {
  const campaignType = useResolvedCampaignType(type);
  return useCampaignsStore((state) => state.autoJoin[campaignType] ?? false);
};
export const useSelectedCampaignId = (type?: CampaignTypes) => {
  const campaignType = useResolvedCampaignType(type);
  return useCampaignsStore((state) => state.getSelectedCampaignId(campaignType));
};
export const useIsHeaderCollapsed = () => {
  const campaignType = useResolvedCampaignType();
  const isCollapsed = useCampaignsStore((state) => state.isHeaderCollapsed[campaignType]);
  const viewMode = useViewMode();
  return viewMode === SINGLE_VIEW_MODE ? isCollapsed : false;
};

export const useIsSingleItem = (type?: CampaignTypes) => {
  const campaignType = useResolvedCampaignType(type);
  const items = useCampaigns(campaignType);
  return items.length === 1;
};
export const usePanelInfo = (type?: CampaignTypes) => {
  const campaignType = useResolvedCampaignType(type);
  return useCampaignsStore((state) => state.getPanelInfo(campaignType));
};
export const usePrizesCount = () => useCampaignsStore((state) => state.getPrizesCount());


export const useHasMany = (type?: CampaignTypes) => {
  const campaignType = useResolvedCampaignType(type);
  return useCampaignsStore((state) => state.hasMany[campaignType] ?? false);
};

export const useCampaignInfoVisible = (id?: string, campaignType?: CampaignTypes) =>
  useCampaignsStore((state) => {
    if (!id) return false;
    if (!campaignType) return false;

    const idFound = state.selectedCampaignIds[campaignType] === id;
    return state.campaignInfoVisible[id] || (state.isHeaderCollapsed[campaignType] && idFound);
  });

/** QA-only — mirrors `useTournamentMockMode`. */
// fallow-ignore-next-line unused-export
export const useCampaignMockMode = () => useCampaignsStore((state) => state.mockMode);

// Actions hook
export const useCampaignActions = () =>
  useCampaignsStore((state) => ({
    setCampaignsFromResponse: state.setCampaignsFromResponse,
    setSelectedCampaignId: state.setSelectedCampaignId,
    setCampaignType: state.setCampaignType,
    toggleHeaderCollapsed: state.toggleHeaderCollapsed,
    setActiveSlot: state.setActiveSlot,
    setPanelInfo: state.setPanelInfo,
    clearCampaigns: state.clearCampaigns,
    updateCampaign: state.updateCampaign,
    reParseTournamentsWithGames: state.reParseTournamentsWithGames,
    setCampaignInfoVisible: state.setCampaignInfoVisible,
    clearCampaignInfoVisible: state.clearCampaignInfoVisible,
    setMockMode: state.setMockMode,
    seedExternalCampaigns: state.seedExternalCampaigns,
    addMockCampaign: state.addMockCampaign,
    removeCampaign: state.removeCampaign,
    updateCampaignById: state.updateCampaignById,
    clearMockCampaigns: state.clearMockCampaigns,
  }));
